import styled from 'styled-components';

import { ActionStatus, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';

// One status maps to exactly one presentation everywhere it appears (UX guide §5).
export type Tone = 'neutral' | 'info' | 'running' | 'review' | 'warning' | 'success' | 'danger';

// `background`/`color` style the tinted/solid pills; `accent` is the single solid color that
// represents the tone in non-pill contexts (legend swatches, the progress-bar segments) — the
// standard olio palette hue for that status. It is never the pill's text color, which for the
// solid `review` pill is white (the bug that made "Ready for Review" swatches invisible).
export const TONE_STYLES: Record<Tone, { background: string; color: string; accent: string }> = {
  neutral: { background: 'rgba(15,18,38,0.06)', color: 'rgba(15,18,38,0.6)', accent: colors.black25 },
  info: { background: colors.primaryBlue10, color: colors.primaryBlue, accent: colors.primaryBlue },
  // AI/automated work in flight — the purple accent shared with the running spinner.
  running: { background: 'rgba(103,51,208,0.1)', color: colors.accentPurple, accent: colors.accentPurple },
  // Ready for a person to act — a light blue tint pill (accent stays solid blue for swatches).
  review: { background: colors.primaryBlue10, color: colors.primaryBlue, accent: colors.primaryBlue },
  // Needs attention — the standard olio secondaryOrange (the swatch/segment accent and pill
  // tint); the pill text is a darker shade of it for readable contrast on the light tint.
  warning: { background: 'rgba(245,130,94,0.16)', color: '#c0552f', accent: colors.secondaryOrange },
  success: { background: colors.accentGreen10, color: colors.accentGreen, accent: colors.accentGreen },
  danger: { background: colors.accentRed10, color: colors.accentRed, accent: colors.accentRed },
};

const WORKFLOW_STATUS_DISPLAY: Record<WorkflowStatus, { label: string; tone: Tone }> = {
  [WorkflowStatus.RUNNING]: { label: 'In Progress', tone: 'info' },
  [WorkflowStatus.ACTION_REQUIRED]: { label: 'Needs Attention', tone: 'warning' },
  [WorkflowStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [WorkflowStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
};

const ACTION_STATUS_DISPLAY: Record<ActionStatus, { label: string; tone: Tone }> = {
  [ActionStatus.PENDING]: { label: 'Pending', tone: 'neutral' },
  [ActionStatus.RUNNING]: { label: 'Running', tone: 'running' },
  [ActionStatus.ACTION_REQUIRED]: { label: 'Ready for Review', tone: 'review' },
  [ActionStatus.FLAGGED]: { label: 'Needs Info', tone: 'warning' },
  [ActionStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [ActionStatus.ERRORED]: { label: 'Error', tone: 'danger' },
  [ActionStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
  [ActionStatus.SKIPPED]: { label: 'Not Applicable', tone: 'neutral' },
};

export function workflowStatusDisplay(status: WorkflowStatus) {
  return WORKFLOW_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function actionStatusDisplay(status: ActionStatus) {
  return ACTION_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const display = workflowStatusDisplay(status);
  return (
    <Pill $tone={display.tone} data-testid='workflow-status'>
      {display.label}
    </Pill>
  );
}

export function ActionStatusBadge({ status }: { status: ActionStatus }) {
  const display = actionStatusDisplay(status);
  return (
    <Pill $tone={display.tone} data-testid='action-status'>
      {display.label}
    </Pill>
  );
}

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 24px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background-color: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;
